import { useEffect, useRef, useState } from 'react';

/**
 * A bounded poll that survives its parent re-rendering.
 *
 * The callback is kept out of the effect's dependencies. A caller may pass it as an inline arrow,
 * with a new identity on every render, and the shell that renders the steps re-renders as a turn
 * runs — its start and end, each document it streams, each study read. An effect keyed on the
 * callback would tear down and rebuild its interval on every one of those, so whether the timer
 * ever reached its delay would depend on how busy the conversation was: re-rendered often enough,
 * the card would stop updating while the researcher talks to the agent as their study fields,
 * which is the demo.
 *
 * So the timer is created once per `active` transition and nothing else. The callback is read
 * through a ref, updated on every render, so the interval always calls the current one without
 * being restarted by it; and the count lives in a ref beside the state, so incrementing it does
 * not invalidate the effect either. The returned flag is what the caller renders — that is the
 * only reason a count is state at all.
 */
export const useBoundedPoll = ({
  active,
  intervalMs,
  limit,
  onTick,
}: {
  active: boolean;
  intervalMs: number;
  limit: number;
  onTick: () => void;
}): boolean => {
  const [ticks, setTicks] = useState(0);
  const ticksRef = useRef(0);
  const onTickRef = useRef(onTick);
  const wasActive = useRef(active);

  // Each start of the watch gets the whole budget: a checkout opened after an earlier one closed
  // in the same mount is watched for as long as the first was, not for what the first left over.
  useEffect(() => {
    if (active && !wasActive.current) {
      ticksRef.current = 0;
      setTicks(0);
    }
    wasActive.current = active;
  }, [active]);

  // Every render, so the interval below always reaches the latest closure without depending on it.
  useEffect(() => {
    onTickRef.current = onTick;
  });

  const exhausted = ticks >= limit;

  useEffect(() => {
    // `exhausted` is in the condition and the dependencies so that reaching the limit **stops the
    // timer** rather than leaving it running over a callback that returns immediately. A tab left
    // open on a wedged study would otherwise wake up every few seconds for the life of the tab to
    // do nothing at all.
    if (!active || exhausted) {
      return;
    }

    const timer = setInterval(() => {
      // Both guards are needed and they do different jobs. This one keeps the count from
      // overshooting: the effect below tears the timer down when `exhausted` flips, but that
      // flip goes through React state, so without this the interval fires once more in between
      // and the limit is exceeded by one. The effect's own condition is what actually stops the
      // timer — this alone would leave it waking up for ever to return immediately.
      if (ticksRef.current >= limit) {
        return;
      }

      ticksRef.current += 1;
      setTicks(ticksRef.current);
      onTickRef.current();
    }, intervalMs);

    return () => clearInterval(timer);
  }, [active, exhausted, intervalMs, limit]);

  return exhausted;
};
